import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Select,
  InputNumber,
  Tag,
  Space,
  Tooltip,
  Switch,
  Spin,
  Empty,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  getBufferConfig,
  updateBufferConfig,
  calculateBuffer,
} from "../../api/wastageApi";

const BufferConfigManager = () => {
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [calcModalVisible, setCalcModalVisible] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [form] = Form.useForm();
  const [calcForm] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getBufferConfig();
      // API may return a single config or an array
      const list = Array.isArray(data)
        ? data
        : data.configs
        ? data.configs
        : [data];
      setConfigs(list.filter((c) => c && c.buffer_pct != null));
    } catch (error) {
      toast.error("Failed to load buffer configuration");
    }
    setLoading(false);
  };

  const handleOpenModal = (config = null) => {
    setEditingConfig(config);
    if (config) {
      form.setFieldsValue(config);
    } else {
      form.resetFields();
      form.setFieldsValue({ is_active: true });
    }
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        id: editingConfig?.id || undefined,
      };
      await updateBufferConfig(payload);
      toast.success(
        editingConfig
          ? "Buffer config updated"
          : "Buffer config created"
      );
      setModalVisible(false);
      fetchData();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to save config"
      );
    }
  };

  const handleCalculate = async (values) => {
    setCalcLoading(true);
    try {
      const result = await calculateBuffer(values);
      setCalcResult(result);
    } catch (error) {
      toast.error("Failed to calculate buffer");
    }
    setCalcLoading(false);
  };

  const columns = [
    {
      title: "Buffer %",
      dataIndex: "buffer_pct",
      key: "buffer_pct",
      render: (v) => <strong>{v}%</strong>,
    },
    {
      title: "Min Buffer Qty",
      dataIndex: "min_buffer_qty",
      key: "min_buffer_qty",
      render: (v) => v || "-",
    },
    {
      title: "Max Buffer %",
      dataIndex: "max_buffer_pct",
      key: "max_buffer_pct",
      render: (v) => (v ? `${v}%` : "-"),
    },
    {
      title: "Ply",
      dataIndex: "ply",
      key: "ply",
      render: (v) => (v ? `${v}-Ply` : "All"),
    },
    {
      title: "Box Type",
      dataIndex: "box_type_id",
      key: "box_type_id",
      render: (v) => v || "All",
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (active) => (
        <Tag color={active ? "green" : "default"}>
          {active ? "ACTIVE" : "INACTIVE"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleOpenModal(record)}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Buffer Configuration"
        extra={
          <Space>
            <Button onClick={() => setCalcModalVisible(true)}>
              Buffer Calculator
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
            >
              Add Config
            </Button>
          </Space>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
          </div>
        ) : configs.length > 0 ? (
          <Table
            columns={columns}
            dataSource={configs}
            rowKey={(r) => r.id || Math.random()}
            pagination={false}
          />
        ) : (
          <Empty description="No buffer configurations yet" />
        )}
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingConfig ? "Edit Buffer Config" : "New Buffer Config"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="buffer_pct"
            label="Buffer Percentage"
            rules={[{ required: true, message: "Enter buffer %" }]}
            tooltip="Extra quantity to produce beyond order requirement"
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              max={50}
              precision={1}
              addonAfter="%"
            />
          </Form.Item>

          <Form.Item
            name="min_buffer_qty"
            label="Min Buffer Quantity"
            tooltip="Minimum extra units regardless of percentage"
          >
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>

          <Form.Item
            name="max_buffer_pct"
            label="Max Buffer Percentage"
            tooltip="Upper cap on buffer percentage"
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              max={50}
              precision={1}
              addonAfter="%"
            />
          </Form.Item>

          <Form.Item name="ply" label="Ply (optional)">
            <Select placeholder="All plies" allowClear>
              <Select.Option value={3}>3-Ply</Select.Option>
              <Select.Option value={5}>5-Ply</Select.Option>
              <Select.Option value={7}>7-Ply</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Buffer Calculator Modal */}
      <Modal
        title="Buffer Calculator"
        open={calcModalVisible}
        onCancel={() => {
          setCalcModalVisible(false);
          setCalcResult(null);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={calcForm} layout="vertical" onFinish={handleCalculate}>
          <Form.Item
            name="quantity"
            label="Order Quantity"
            rules={[{ required: true }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              placeholder="e.g. 5000"
            />
          </Form.Item>
          <Form.Item name="ply" label="Ply">
            <Select placeholder="Select ply" allowClear>
              <Select.Option value={3}>3-Ply</Select.Option>
              <Select.Option value={5}>5-Ply</Select.Option>
              <Select.Option value={7}>7-Ply</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={calcLoading}
              block
            >
              Calculate
            </Button>
          </Form.Item>
        </Form>

        {calcResult && (
          <Card size="small" style={{ marginTop: 16, background: "#f6ffed" }}>
            <p>
              <strong>Buffer %:</strong> {calcResult.buffer_pct}%
            </p>
            <p>
              <strong>Buffer Qty:</strong> {calcResult.buffer_qty}
            </p>
            <p>
              <strong>Production Qty:</strong>{" "}
              {calcResult.production_qty?.toLocaleString()}
            </p>
          </Card>
        )}
      </Modal>
    </div>
  );
};

export default BufferConfigManager;
